import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { AuthorText } from '../../components/AuthorText';
import { Credits } from '../../components/Credits';
import { Steve } from '../../components/Steve';
import { VuMeter } from '../../components/VuMeter';
import { useMixerModel } from '../../audio/useMixerModel';
import { S, faq, t, type Leaf } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines, useLogLines } from '../../state/derived';
import { AquaDialog } from './AquaDialog';
import { AquaMenu, type AquaMenuDef } from './AquaMenu';
import {
  DESK_START,
  DESK_WINDOWS,
  closeWindow,
  minimise,
  minimiseAll,
  openCount,
  restore,
  restoreAll,
  taskbarWindows,
  toggleZoom,
  type Desk,
  type DeskWindow,
} from './desk';
import styles from './aqua.module.css';

/** Les cinq bulles de fond (taille, position, durée, retard). */
const BUBBLES = [
  { size: 11, left: '6%', alpha: 32, duration: '11s', delay: '0s' },
  { size: 6, left: '19%', alpha: 28, duration: '14s', delay: '2s' },
  { size: 17, left: '41%', alpha: 20, duration: '12s', delay: '4s' },
  { size: 8, left: '67%', alpha: 30, duration: '15s', delay: '1s' },
  { size: 10, left: '88%', alpha: 24, duration: '13s', delay: '6s' },
] as const;

/** Les trois algues du bac. */
const WEEDS = [
  { side: 'left', pos: '4%', width: 26, height: 120, alpha: 35, duration: '6s', delay: '0s' },
  { side: 'left', pos: '11%', width: 18, height: 84, alpha: 25, duration: '7.5s', delay: '.8s' },
  { side: 'right', pos: '6%', width: 22, height: 100, alpha: 28, duration: '8s', delay: '1.6s' },
] as const;

/** Sept flocons de nourriture. */
const FLAKES = [
  { left: '38%', delay: '0s' },
  { left: '44%', delay: '.12s' },
  { left: '50%', delay: '.05s' },
  { left: '56%', delay: '.22s' },
  { left: '61%', delay: '.3s' },
  { left: '47%', delay: '.42s' },
  { left: '53%', delay: '.55s' },
] as const;

/** Cinq losanges de compliment, toujours au-dessus de la silhouette. */
const PRAISES = [
  { size: 18, left: '36%', bottom: '66%', delay: '0s' },
  { size: 14, left: '46%', bottom: '72%', delay: '.18s' },
  { size: 20, left: '56%', bottom: '68%', delay: '.32s' },
  { size: 13, left: '64%', bottom: '74%', delay: '.46s' },
  { size: 16, left: '50%', bottom: '78%', delay: '.6s' },
] as const;

/** Huit bulles émises par Steve. */
const BURSTS = [
  { size: 10, left: '58%', top: '46%', delay: '0s' },
  { size: 7, left: '60%', top: '48%', delay: '.14s' },
  { size: 13, left: '57%', top: '44%', delay: '.26s' },
  { size: 8, left: '59%', top: '47%', delay: '.38s' },
  { size: 11, left: '61%', top: '45%', delay: '.5s' },
  { size: 6, left: '58%', top: '49%', delay: '.62s' },
  { size: 9, left: '60%', top: '43%', delay: '.74s' },
  { size: 12, left: '57%', top: '47%', delay: '.86s' },
] as const;

/** Décalages de la dérive des fenêtres, dans l'ordre du handoff. */
const DRIFT = ['10s', '12s', '11s', '13s', '14s', '12s', '13s'] as const;
const DRIFT_DELAY = ['0s', '.6s', '1.2s', '.3s', '.9s', '1.5s', '1.9s'] as const;

interface WindowProps {
  readonly id: DeskWindow;
  readonly title: string;
  readonly state?: string;
  readonly index: number;
  readonly desk: Desk;
  readonly lang: Parameters<typeof t>[1];
  readonly holder: RefObject<HTMLElement | null>;
  readonly onAct: (id: DeskWindow, act: 'zoom' | 'minimise' | 'close') => void;
  readonly children: ReactNode;
}

/**
 * Une fenêtre du bac.
 *
 * **Les trois pastilles ne sont plus des décorations.** Le handoff les donnait
 * pour telles, et c'est exactement le genre de faux bouton qu'on ne veut plus :
 * bleu élargit, jaune réduit à la barre de titre, orange ferme. Rien ne se perd
 * — une fenêtre fermée reste dans la barre des tâches et dans le menu
 * « Fenêtres ».
 */
function Win({
  id,
  title,
  state,
  index,
  desk,
  lang,
  holder,
  onAct,
  children,
}: WindowProps): ReactNode {
  const style = {
    '--drift-duration': DRIFT[index % DRIFT.length],
    '--drift-delay': DRIFT_DELAY[index % DRIFT_DELAY.length],
  } as CSSProperties;

  const item = desk[id];
  if (item.mode === 'closed') return null;
  const minimised = item.mode === 'minimised';

  return (
    <section
      ref={holder}
      className={cx(styles.window, item.zoomed && styles.wide, minimised && styles.minimised)}
      style={style}
    >
      <h2 className={styles.titlebar}>
        <span>{title}</span>
        <span className={styles.dots}>
          <button
            type="button"
            className={cx(styles.dot, styles.dotBlue)}
            aria-label={`${t(S.aqua.dots.zoom, lang)} — ${title}`}
            aria-pressed={item.zoomed}
            onClick={() => {
              onAct(id, 'zoom');
            }}
          />
          <button
            type="button"
            className={cx(styles.dot, styles.dotYellow)}
            aria-label={`${t(S.aqua.dots.minimise, lang)} — ${title}`}
            aria-pressed={minimised}
            onClick={() => {
              onAct(id, 'minimise');
            }}
          />
          <button
            type="button"
            className={cx(styles.dot, styles.dotOrange)}
            aria-label={`${t(S.aqua.dots.close, lang)} — ${title}`}
            onClick={() => {
              onAct(id, 'close');
            }}
          />
        </span>
        {state !== undefined && <span className={styles.titlebarState}>{state}</span>}
      </h2>
      {!minimised && children}
    </section>
  );
}

/** Une boîte de dialogue, désignée par ses deux chaînes. */
interface DialogContent {
  readonly title: Leaf;
  readonly body: Leaf;
}

/**
 * Skin 1 — AquariumOS : Steve en direct, son bac, ses outils.
 *
 * **Aucun faux bouton.** La barre de menu, le bouton Démarrer, les entrées de
 * la barre des tâches, les pastilles de chaque barre de titre et les relevés du
 * bac agissent tous. C'est la règle qu'on s'était donnée pour le skin xp, et il
 * n'y avait pas de raison que le premier skin du site y échappe : une chrome
 * qui ne répond pas se remarque immédiatement.
 */
export function AquaSkin(): ReactNode {
  const state = useSteve();
  const actions = useActions();
  const { lang, seen, meals, clock, draft, followCursor, feedFx, praiseFx, bubbleFx } = state;
  const mixer = useMixerModel();
  const audio = useAudioText();
  const log = useLogLines();
  const guestbook = useGuestbookLines();

  const [desk, setDesk] = useState<Desk>(DESK_START);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogContent | null>(null);

  // Une ancre par fenêtre : « aller à » veut dire faire défiler jusqu'à elle.
  const refs: Record<DeskWindow, RefObject<HTMLElement | null>> = {
    live: useRef<HTMLElement>(null),
    log: useRef<HTMLElement>(null),
    monitor: useRef<HTMLElement>(null),
    lore: useRef<HTMLElement>(null),
    mixer: useRef<HTMLElement>(null),
    faq: useRef<HTMLElement>(null),
    guestbook: useRef<HTMLElement>(null),
  };

  /** Ouvre une boîte de dialogue, en refermant le menu qui l'a demandée. */
  const say = useCallback((content: DialogContent): void => {
    setOpenMenu(null);
    setDialog(content);
  }, []);

  /**
   * Ramène une fenêtre à l'écran **puis** la fait défiler sous les yeux.
   *
   * Les deux vont ensemble : restaurer une fenêtre restée trois écrans plus bas
   * donne l'impression que le clic n'a rien fait. Le défilement attend l'image
   * suivante, sinon il vise la position d'avant la réapparition.
   */
  const goTo = useCallback((id: DeskWindow): void => {
    setDesk((current) => restore(current, id));
    window.requestAnimationFrame(() => {
      refs[id].current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs est stable, ses cases ne changent jamais.
  }, []);

  const onAct = useCallback((id: DeskWindow, act: 'zoom' | 'minimise' | 'close'): void => {
    setDesk((current) => {
      if (act === 'zoom') return toggleZoom(current, id);
      if (act === 'minimise') return minimise(current, id);
      return closeWindow(current, id);
    });
  }, []);

  /** Le titre affiché d'une fenêtre — même chaîne dans la barre et le menu. */
  const titles = useMemo<Record<DeskWindow, string>>(
    () => ({
      live: t(S.aqua.win_live, lang),
      log: t(S.aqua.win_log, lang),
      monitor: t(S.aqua.win_monitor, lang),
      lore: t(S.aqua.win_lore, lang),
      mixer: t(S.mixer.window, lang),
      faq: t(S.aqua.win_faq, lang),
      guestbook: t(S.aqua.win_guestbook, lang),
    }),
    [lang],
  );

  // ── Les quatre menus de la barre du haut ────────────────────────────────
  const menus = useMemo<readonly AquaMenuDef[]>(
    () => [
      {
        id: 'file',
        label: S.aqua.menu_file,
        items: [
          {
            id: 'new',
            label: S.aqua.menus.file_new,
            onSelect: () => {
              say({ title: S.aqua.dialogs.new_title, body: S.aqua.dialogs.new_body });
            },
          },
          {
            id: 'export',
            label: S.aqua.menus.file_export,
            onSelect: () => {
              say({ title: S.aqua.dialogs.export_title, body: S.aqua.dialogs.export_body });
            },
          },
          {
            id: 'props',
            label: S.aqua.menus.file_props,
            onSelect: () => {
              say({ title: S.aqua.dialogs.props_title, body: S.aqua.dialogs.props_body });
            },
          },
          {
            id: 'follower',
            label: S.aqua.menus.file_follower,
            checked: followCursor,
            onSelect: actions.toggleFollower,
          },
          {
            id: 'quit',
            label: S.aqua.menus.file_quit,
            onSelect: () => {
              say({ title: S.aqua.dialogs.quit_title, body: S.aqua.dialogs.quit_body });
            },
          },
        ],
      },
      {
        id: 'feed',
        label: S.aqua.menu_feed,
        items: [
          { id: 'flakes', label: S.aqua.menus.feed_flakes, onSelect: actions.feed },
          { id: 'praise', label: S.aqua.menus.feed_praise, onSelect: actions.praise },
          { id: 'bubbles', label: S.aqua.menus.feed_bubbles, onSelect: actions.bubbles },
          { id: 'song', label: S.aqua.menus.feed_song, onSelect: actions.toggleAudio },
          {
            id: 'diet',
            label: S.aqua.menus.feed_diet,
            onSelect: () => {
              say({ title: S.aqua.dialogs.diet_title, body: S.aqua.dialogs.diet_body });
            },
          },
        ],
      },
      {
        id: 'windows',
        label: S.aqua.menu_windows,
        items: [
          // Une entrée par fenêtre : la coche dit celles qui sont à l'écran, et
          // choisir une fenêtre fermée la rouvre. Rien n'est donc perdu.
          ...DESK_WINDOWS.map((id) => ({
            id,
            label: { fr: titles[id], en: titles[id] },
            checked: desk[id].mode === 'open',
            onSelect: () => {
              goTo(id);
            },
          })),
          {
            id: 'restore-all',
            label: S.aqua.menus.win_restore,
            onSelect: () => {
              setDesk(restoreAll);
            },
          },
          {
            id: 'minimise-all',
            label: S.aqua.menus.win_minimise,
            onSelect: () => {
              setDesk(minimiseAll);
            },
          },
        ],
      },
      {
        id: 'help',
        label: S.aqua.menu_help,
        items: [
          {
            id: 'about',
            label: S.aqua.menus.help_about,
            onSelect: () => {
              say({ title: S.aqua.dialogs.about_title, body: S.aqua.dialogs.about_body });
            },
          },
          {
            id: 'manual',
            label: S.aqua.menus.help_manual,
            onSelect: () => {
              say({ title: S.aqua.dialogs.manual_title, body: S.aqua.dialogs.manual_body });
            },
          },
          {
            id: 'faq',
            label: S.aqua.menus.help_faq,
            onSelect: () => {
              goTo('faq');
            },
          },
          {
            id: 'support',
            label: S.aqua.menus.help_support,
            onSelect: () => {
              say({ title: S.aqua.dialogs.support_title, body: S.aqua.dialogs.support_body });
            },
          },
          {
            id: 'skin',
            label: S.aqua.menus.help_skin,
            onSelect: () => {
              actions.cycleSkin(0);
            },
          },
        ],
      },
    ],
    [actions, desk, followCursor, goTo, say, titles],
  );

  // ── Le menu du bouton Démarrer ──────────────────────────────────────────
  const startMenu = useMemo<readonly AquaMenuDef[]>(
    () => [
      {
        id: 'start',
        label: S.aqua.start,
        items: [
          {
            id: 'live',
            label: S.aqua.menus.start_live,
            onSelect: () => {
              goTo('live');
            },
          },
          {
            id: 'mixer',
            label: S.aqua.menus.start_mixer,
            onSelect: () => {
              goTo('mixer');
            },
          },
          {
            id: 'guestbook',
            label: S.aqua.menus.start_guest,
            onSelect: () => {
              goTo('guestbook');
            },
          },
          {
            id: 'probe',
            label: S.aqua.menus.start_probe,
            onSelect: () => {
              say({ title: S.aqua.dialogs.probe_title, body: S.aqua.dialogs.probe_body });
            },
          },
          {
            id: 'skin',
            label: S.aqua.menus.start_skin,
            onSelect: () => {
              actions.cycleSkin(0);
            },
          },
          {
            id: 'off',
            label: S.aqua.menus.start_off,
            onSelect: () => {
              say({ title: S.aqua.dialogs.off_title, body: S.aqua.dialogs.off_body });
            },
          },
        ],
      },
    ],
    [actions, goTo, say],
  );

  return (
    <div className={styles.root}>
      <div className={styles.caustics} aria-hidden="true" />
      {BUBBLES.map((bubble, index) => (
        <div
          key={index}
          aria-hidden="true"
          className={styles.bubble}
          style={
            {
              left: bubble.left,
              '--bubble-size': `${String(bubble.size)}px`,
              '--bubble-color': `rgb(255 255 255 / ${String(bubble.alpha)}%)`,
              '--bubble-duration': bubble.duration,
              '--bubble-delay': bubble.delay,
            } as CSSProperties
          }
        />
      ))}

      <header className={styles.menubar}>
        <div className={styles.menuSide}>
          <button
            type="button"
            className={styles.brand}
            onClick={() => {
              say({ title: S.aqua.dialogs.about_title, body: S.aqua.dialogs.about_body });
            }}
          >
            ◉ {t(S.aqua.os_name, lang)}
          </button>
          <AquaMenu menus={menus} open={openMenu} lang={lang} onOpen={setOpenMenu} />
        </div>
        <div className={cx(styles.menuSide, styles.menuRight)}>
          {/* Les deux sondes du bac : cliquables elles aussi, elles répondent
              par la boîte d'étalonnage. Un relevé qui ne bouge jamais et qu'on
              ne peut pas interroger est un décor, pas un instrument. */}
          <button
            type="button"
            className={styles.menuStat}
            title={t(S.aqua.probe_hint, lang)}
            onClick={() => {
              say({ title: S.aqua.dialogs.probe_title, body: S.aqua.dialogs.probe_body });
            }}
          >
            {t(S.aqua.ph, lang)}
          </button>
          <button
            type="button"
            className={styles.menuStat}
            title={t(S.aqua.probe_hint, lang)}
            onClick={() => {
              say({ title: S.aqua.dialogs.probe_title, body: S.aqua.dialogs.probe_body });
            }}
          >
            {t(S.aqua.temp, lang)}
          </button>
          <button
            type="button"
            className={styles.menuStat}
            onClick={() => {
              goTo('mixer');
            }}
          >
            {t(S.mixer.window, lang).replace('.exe', '')} {mixer.sliders[0]?.valueLabel}
          </button>
          <span className={styles.clock}>{clock}</span>
        </div>
      </header>

      <main className={styles.main}>
        <div className={styles.row}>
          {/* 1. steve_live.exe */}
          <Win id="live" title={titles.live} index={0} desk={desk} lang={lang} holder={refs.live} onAct={onAct}>
            <div className={styles.live}>
              <div className={cx(styles.badge, styles.badgeLive)}>{t(S.aqua.live_badge, lang)}</div>
              <div className={cx(styles.badge, styles.badgeCam)}>{t(S.aqua.live_cam, lang)}</div>

              {WEEDS.map((weed, index) => (
                <div
                  key={index}
                  aria-hidden="true"
                  className={styles.weed}
                  style={
                    {
                      [weed.side]: weed.pos,
                      '--weed-width': `${String(weed.width)}px`,
                      '--weed-height': `${String(weed.height)}px`,
                      '--weed-color': `rgb(52 211 153 / ${String(weed.alpha)}%)`,
                      '--weed-duration': weed.duration,
                      '--weed-delay': weed.delay,
                    } as CSSProperties
                  }
                />
              ))}

              <div className={styles.steveHolder}>
                <Steve bobDuration="3s" onClick={actions.bump} label={t(S.common.title, lang)} />
              </div>

              {feedFx > 0 && (
                <div key={feedFx} className={styles.fxLayer} aria-hidden="true">
                  {FLAKES.map((flake, index) => (
                    <span
                      key={index}
                      className={styles.flake}
                      style={
                        { '--fx-left': flake.left, '--fx-delay': flake.delay } as CSSProperties
                      }
                    />
                  ))}
                </div>
              )}

              {praiseFx > 0 && (
                <div key={praiseFx} className={styles.fxLayer} aria-hidden="true">
                  {PRAISES.map((praise, index) => (
                    <span
                      key={index}
                      className={styles.praise}
                      style={
                        {
                          '--fx-left': praise.left,
                          '--fx-bottom': praise.bottom,
                          '--fx-size': `${String(praise.size)}px`,
                          '--fx-delay': praise.delay,
                        } as CSSProperties
                      }
                    />
                  ))}
                </div>
              )}

              {bubbleFx > 0 && (
                <div key={bubbleFx} className={styles.fxLayer} aria-hidden="true">
                  {BURSTS.map((burst, index) => (
                    <span
                      key={index}
                      className={styles.burst}
                      style={
                        {
                          '--fx-left': burst.left,
                          '--fx-top': burst.top,
                          '--fx-size': `${String(burst.size)}px`,
                          '--fx-delay': burst.delay,
                        } as CSSProperties
                      }
                    />
                  ))}
                </div>
              )}

              <div className={styles.sand} aria-hidden="true" />
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={cx(styles.button, styles.buttonPrimary)}
                onClick={actions.toggleAudio}
              >
                {audio.label}
              </button>
              <button type="button" className={styles.button} onClick={actions.feed}>
                {t(S.aqua.btn_feed, lang)}
              </button>
              <button type="button" className={styles.button} onClick={actions.praise}>
                {t(S.aqua.btn_praise, lang)}
              </button>
              <button type="button" className={styles.button} onClick={actions.bubbles}>
                {t(S.aqua.btn_bubbles, lang)}
              </button>
              <p className={styles.audioHint} role="status">
                {audio.hint}
              </p>
            </div>
          </Win>

          {/* 2. journal_du_bac.log */}
          <Win id="log" title={titles.log} index={1} desk={desk} lang={lang} holder={refs.log} onAct={onAct}>
            <ul className={styles.log}>
              {log.map((line) => (
                <li key={line.id} className={styles.logLine}>
                  <span className={styles.logMark} aria-hidden="true">
                    &gt;
                  </span>
                  <span>{line.text}</span>
                </li>
              ))}
            </ul>
          </Win>

          {/* 3. moniteur.app */}
          <Win id="monitor" title={titles.monitor} index={2} desk={desk} lang={lang} holder={refs.monitor} onAct={onAct}>
            <div className={styles.monitor}>
              <div>
                <div className={cx(styles.metricValue, styles.metricAccent)}>{seen}</div>
                <div className={styles.metricLabel}>{t(S.common.seen_today, lang)}</div>
              </div>
              <div>
                <div className={styles.metricValue}>2</div>
                <div className={styles.metricLabel}>{t(S.common.legs_live, lang)}</div>
              </div>
              <div>
                <div className={styles.metricValue}>100 %</div>
                <div className={styles.metricLabel}>{t(S.common.orange_pct, lang)}</div>
              </div>
              <div>
                <div className={styles.metricValue}>{meals}</div>
                <div className={styles.metricLabel}>{t(S.common.meals, lang)}</div>
              </div>
            </div>
          </Win>
        </div>

        <div className={styles.row}>
          {/* 4. lore_de_steve.txt */}
          <Win id="lore" title={titles.lore} index={3} desk={desk} lang={lang} holder={refs.lore} onAct={onAct}>
            <div className={styles.lore}>
              <p>
                <AuthorText linkClassName={styles.creditLink}>{t(S.aqua.lore_p1, lang)}</AuthorText>
              </p>
              <p>{t(S.aqua.lore_p2, lang)}</p>
              <div className={styles.timeline}>
                <div className={styles.timelineRow}>
                  <span className={styles.timelineDate}>02.04.25</span>
                  <span>{t(S.aqua.timeline_1, lang)}</span>
                </div>
                <div className={styles.timelineRow}>
                  <span className={styles.timelineDate}>05.04.25</span>
                  <span>{t(S.aqua.timeline_2, lang)}</span>
                </div>
                <div className={styles.timelineRow}>
                  <span className={styles.timelineDate}>{t(S.aqua.timeline_month, lang)}</span>
                  <span>{t(S.aqua.timeline_3, lang)}</span>
                </div>
                <div className={styles.timelineRow}>
                  <span className={styles.timelineDate}>{t(S.aqua.timeline_today, lang)}</span>
                  <span>{t(S.aqua.timeline_4, lang)}</span>
                </div>
              </div>
            </div>
          </Win>

        </div>

        <div className={styles.row}>
          {/* 6. mixeur.exe */}
          <Win id="mixer" title={titles.mixer} state={mixer.stateLabel} index={5} desk={desk} lang={lang} holder={refs.mixer} onAct={onAct}>
            <div className={styles.mixer}>
              <VuMeter className={styles.vu} barClassName={styles.vuBar} />

              <div className={styles.pads}>
                {mixer.pads.map((pad) => (
                  <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                    <span>{pad.label}</span>
                    <span className={styles.padTime}>{pad.timeLabel}</span>
                  </button>
                ))}
              </div>

              <div className={styles.controls}>
                <button
                  type="button"
                  className={cx(styles.button, styles.buttonPrimary, styles.buttonSmall)}
                  onClick={mixer.onToggleHold}
                >
                  {mixer.holdLabel}
                </button>
                <button
                  type="button"
                  className={cx(styles.button, styles.buttonSmall, styles.pushRight)}
                  onClick={mixer.onStop}
                >
                  {mixer.stopLabel}
                </button>
              </div>

              {mixer.sliders.map((slider) => (
                <div key={slider.key} className={styles.slider}>
                  <label className={styles.sliderLabel} htmlFor={`aqua-${slider.key}`}>
                    {slider.label}
                  </label>
                  <input
                    id={`aqua-${slider.key}`}
                    type="range"
                    className={styles.sliderInput}
                    min={slider.min}
                    max={slider.max}
                    step={slider.step}
                    value={slider.value}
                    onChange={(event) => {
                      slider.onChange(event.currentTarget.valueAsNumber);
                    }}
                  />
                  <output className={styles.sliderValue} htmlFor={`aqua-${slider.key}`}>
                    {slider.valueLabel}
                  </output>
                </div>
              ))}

              <div className={styles.presets}>
                {mixer.presets.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    className={cx(styles.button, styles.buttonSmall)}
                    onClick={preset.onSelect}
                  >
                    {preset.label}
                  </button>
                ))}
                <button
                  type="button"
                  className={cx(
                    styles.button,
                    styles.buttonPrimary,
                    styles.buttonSmall,
                    styles.pushRight,
                  )}
                  onClick={mixer.onToggleWobble}
                >
                  {mixer.wobbleLabel}
                </button>
              </div>
            </div>
          </Win>

          {/* 7. faq.txt */}
          <Win id="faq" title={titles.faq} index={6} desk={desk} lang={lang} holder={refs.faq} onAct={onAct}>
            <div className={styles.faq}>
              {faq(lang).map((item) => (
                <div key={item.question}>
                  <p className={styles.faqQuestion}>{item.question}</p>
                  <p className={styles.faqAnswer}>{item.answer}</p>
                </div>
              ))}
            </div>
          </Win>
        </div>

        {/* 8. livre_dor.exe */}
        <Win id="guestbook" title={titles.guestbook} index={0} desk={desk} lang={lang} holder={refs.guestbook} onAct={onAct}>
          <div className={styles.guestbook}>
            <ul className={styles.entries}>
              {guestbook.map((entry) => (
                <li key={entry.id} className={styles.entry}>
                  <div className={styles.entryWho}>{entry.who}</div>
                  <div className={styles.entryText}>{entry.text}</div>
                </li>
              ))}
            </ul>
            <div className={styles.compose}>
              <input
                type="text"
                className={styles.input}
                value={draft}
                placeholder={t(S.guestbook.placeholder, lang)}
                aria-label={t(S.guestbook.placeholder, lang)}
                onChange={(event) => {
                  actions.setDraft(event.currentTarget.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') actions.postEntry();
                }}
              />
              <button type="button" className={styles.publish} onClick={actions.postEntry}>
                {t(S.guestbook.publish, lang)}
              </button>
            </div>
          </div>
        </Win>

        <div className={styles.credits}>
          <Credits linkClassName={styles.creditLink} unofficial />
          <br />
          {t(S.common.egg_hint, lang)}
        </div>
      </main>

      <footer className={styles.taskbar}>
        <span className={cx(styles.start, styles.startHolder)}>
          <AquaMenu
            menus={startMenu}
            open={openMenu}
            lang={lang}
            onOpen={setOpenMenu}
            upward
          />
        </span>

        {/* Une entrée par fenêtre encore à l'écran. Un clic la ramène et l'y
            fait défiler ; une fenêtre réduite se distingue à son libellé pâle. */}
        {taskbarWindows(desk).map((id) => (
          <button
            key={id}
            type="button"
            className={cx(
              styles.taskbarItem,
              desk[id].mode === 'minimised' && styles.taskbarItemMin,
            )}
            aria-pressed={desk[id].mode === 'open'}
            onClick={() => {
              goTo(id);
            }}
          >
            {titles[id]}
          </button>
        ))}

        <span className={styles.taskbarCount}>
          {t(S.aqua.taskbar_open, lang, { n: openCount(desk) })} · {t(S.common.seen_today, lang)} :{' '}
          {seen}
        </span>
      </footer>

      {dialog && (
        <AquaDialog
          title={dialog.title}
          body={dialog.body}
          lang={lang}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}
